import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";

import {
	AuthContext,
	type AuthContextValue,
	type AuthStatus,
} from "./auth-context-value";
import { authService } from "./auth.service";
import { onSessionExpired } from "./session-events";
import type {
	AuthUser,
	ChangePasswordPayload,
	LoginCredentials,
} from "./types";
import { useWikiQaStore } from "@/features/wiki-qa";

export function AuthProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState<AuthUser | null>(null);
	const [status, setStatus] = useState<AuthStatus>("loading");

	const refresh = useCallback(async () => {
		let current: AuthUser | null = null;
		try {
			current = await authService.me();
		} catch {
			current = null;
		}
		if (!current) {
			// An expiry discovered at boot emits no sessionExpired event —
			// authService.me() has no interceptor and a login page issues no
			// /api/* call — so the persisted history would otherwise rehydrate
			// into the next agent's shift on a shared workstation.
			useWikiQaStore.getState().clear();
		}
		setUser(current);
		setStatus(current ? "authenticated" : "unauthenticated");
	}, []);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	useEffect(() => {
		return onSessionExpired(() => {
			// A server-side expiry never calls logout(), so this history reset
			// has to be duplicated here rather than shared.
			useWikiQaStore.getState().clear();
			setUser(null);
			setStatus("unauthenticated");
		});
	}, []);

	const login = useCallback(async (credentials: LoginCredentials) => {
		// A new session must never inherit the previous one's history, whatever
		// ended it. Cleared before the request so a failed login cannot leave
		// another agent's questions on screen either.
		useWikiQaStore.getState().clear();
		const next = await authService.login(credentials);
		setUser(next);
		setStatus("authenticated");
		return next;
	}, []);

	const logout = useCallback(async () => {
		await authService.logout();
		// Session-scoped wiki history must not survive into the next agent's
		// session on a shared workstation.
		useWikiQaStore.getState().clear();
		setUser(null);
		setStatus("unauthenticated");
	}, []);

	const changePassword = useCallback(async (payload: ChangePasswordPayload) => {
		const next = await authService.changePassword(payload);
		setUser(next);
		setStatus("authenticated");
		return next;
	}, []);

	const value = useMemo<AuthContextValue>(
		() => ({ status, user, login, logout, changePassword, refresh }),
		[status, user, login, logout, changePassword, refresh]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
